import classNames from 'classnames';
import React, { useState } from 'react';
import { Waypoint } from 'react-waypoint';
import Caret from '../components/Caret';
import type { FetchMoreHandler } from '../components/InfiniteScrollWaypoint';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import SVGIcon from '../components/SVGIcon';
import libraryIcon from '../icons/prefilled/libraryIcon.svg';
import type { TreeListTopic } from '../topic/toTopicTreeList';
import TemplateSidebarItem from './TemplateSidebarItem';
import styles from './TemplatesSidebar.module.scss';
import TemplatesSidebarTopics from './TemplatesSidebarTopics';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const TEMPLATE_POSTS_FRAGMENT = gql(`
  fragment TemplatesSidebar_templatePosts on Post {
    id
    ...TemplateSidebarItem_post
  }
`);

const TOPIC_FRAGMENT = gql(`
  fragment TemplatesSidebar_topic on Topic {
    id
    ...TemplatesSidebarTopics_topic
  }
`);

interface TemplatesSidebarProps {
  activeTemplateId: string | null;
  mobileOpen: boolean;
  topicsTree: TreeListTopic<DocumentType<typeof TOPIC_FRAGMENT>>[];
  userTemplates: DocumentType<typeof TEMPLATE_POSTS_FRAGMENT>[];
  fetchMore?: FetchMoreHandler | null;
  loading: boolean;
  onClickTemplate: (id: string) => void;
}

const TemplatesSidebar = ({
  activeTemplateId,
  mobileOpen,
  topicsTree,
  userTemplates,
  loading,
  onClickTemplate,
  fetchMore,
}: TemplatesSidebarProps) => {
  const [isScrolled, setIsScrolled] = useState(false);

  return (
    <nav
      className={classNames(styles.sidebar, {
        [styles.mobileOpen]: mobileOpen,
      })}
    >
      <div
        className={classNames(styles.title, { [styles.scrolled]: isScrolled })}
      >
        <h3>Templates</h3>
      </div>

      <div className={styles.content}>
        <Waypoint
          onEnter={() => {
            setIsScrolled(false);
          }}
          onLeave={() => {
            setIsScrolled(true);
          }}
        />
        <div className={styles.sectionsContainer}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Mine</div>
            {userTemplates.length > 0 ? (
              userTemplates.map((template) => (
                <TemplateSidebarItem
                  key={template.id}
                  active={activeTemplateId === template.id}
                  template={template}
                  onClick={() => onClickTemplate(template.id)}
                />
              ))
            ) : (
              <>
                {!loading && (
                  <div className={styles.sectionNote}>
                    You haven&apos;t created any templates
                  </div>
                )}
              </>
            )}
            <InfiniteScrollWaypoint fetchMore={fetchMore} />
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>My Topics</div>
            {topicsTree.length > 0 ? (
              <TemplatesSidebarTopics
                topicsTree={topicsTree}
                activeTemplateId={activeTemplateId}
                onClickTemplate={onClickTemplate}
              />
            ) : (
              <>
                {!loading && (
                  <div className={styles.sectionNote}>
                    No templates found in your topics
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <a
          className={styles.libraryCallout}
          target="_blank"
          rel="noopener noreferrer"
          href="https://go.slab.com/slab-library"
        >
          <div className={styles.libraryCalloutIcon}>
            <SVGIcon>{libraryIcon}</SVGIcon>
          </div>
          <div className={styles.libraryCalloutTitle}>The Slab Library</div>
          <div className={styles.libraryCalloutNote}>
            Browse examples from leaders in the industry
          </div>
          <div className={styles.libraryCalloutCaret}>
            <Caret direction="right" size="small" />
          </div>
        </a>
      </div>
    </nav>
  );
};

export default TemplatesSidebar;
